<template>
  <div
    ref="gridComponentRootRef"
    class="directory-list-view"
    tabindex="-1"
    @click.self="grid.handleClickOnContainer"
    @focusin="grid.handleComponentFocusIn"
    @focusout="grid.handleComponentFocusOut"
    @keydown="grid.handleKeyDown"
  >
    <!-- Custom Drag Image Element -->
    <div v-if="grid.isDragging" ref="customDragImageRef" class="custom-drag-preview">
      <component
        :is="grid.dragPreviewIconComponent"
        v-if="grid.dragPreviewIconComponent"
        class="drag-preview-icon"
        aria-hidden="true"
      />
      <span class="drag-preview-name">{{ grid.dragPreviewName }}</span>
      <span v-if="grid.dragPreviewCount > 1" class="drag-preview-badge">{{
        grid.dragPreviewCount
      }}</span>
    </div>

    <table
      ref="gridTableRef"
      class="grid-table"
      :aria-label="ariaLabel || 'Directory content'"
      :aria-rowcount="directoryItems.length > 0 ? directoryItems.length : -1"
      :aria-colcount="grid.localHeaders.length + 1"
      :aria-activedescendant="grid.activeRowId"
      @focus="grid.onGridFocus"
    >
      <thead v-if="showHeaders" class="header-group">
        <tr class="header-row" :style="{ 'grid-template-columns': grid.columnsTemplate }">
          <th
            v-for="header in grid.localHeaders"
            :key="header.key"
            :class="[
              'column',
              'header',
              {
                'header--sortable': header.sortable !== false
              }
            ]"
            scope="col"
            :aria-sort="grid.getAriaSort(header)"
            @click="grid.handleSort(header)"
            @keydown.enter.prevent="grid.handleSort(header)"
            @keydown.space.prevent="grid.handleSort(header)"
          >
            <TextBase
              type="paragraph"
              weight="bold"
              :text="t(`general__text__${header.translationKey}`)"
            />
            <ChevronToggle
              v-if="header.sortable !== false"
              :state="
                header.sortDirection ? { asc: 'up' as const, desc: 'down' as const }[header.sortDirection] : undefined
              "
              class="header-chevron"
              aria-hidden="true"
            />
          </th>
          <th class="column header" scope="col" aria-label="Actions"></th>
        </tr>
      </thead>
      <TransitionGroup is="tbody" ref="gridBodyRef" class="grid-body" name="list">
        <tr
          v-for="(item, index) in directoryItems"
          :id="`row-${item.id}`"
          :key="item.id"
          class="data-row"
          :style="{ 'grid-template-columns': grid.columnsTemplate }"
          :class="{
            'row--selected': grid.selectedItemIds.includes(item.id),
            'row--focused':
              index === grid.focusedRowIndex && grid.gridHasFocus && grid.isKeyboardNavigating,
            'row--dragging-source':
              grid.isDragging && grid.currentlyDraggedItemIds.includes(item.id),
            'row--drop-target': grid.dropTargetItemId === item.id,
            'row--disabled': disabledTypes.includes(item.type),
            'row--archived': item.archived
          }"
          :aria-selected="grid.selectedItemIds.includes(item.id)"
          :aria-label="item.name"
          :aria-rowindex="index + 1"
          :draggable="dragDropEnabled"
          @mouseenter="grid.handleMouseEnterRow(item)"
          @mouseleave="grid.handleMouseLeaveRow()"
          @dragstart="grid.handleDragStart(item, $event)"
          @dragend="grid.handleDragEnd"
          @dragover.prevent="grid.handleDragOverItem(item, $event)"
          @dragleave="grid.handleDragLeaveItem"
          @drop.prevent="grid.handleDropOnItem(item, $event)"
          @click="grid.handleClickOnRow($event, item, index)"
          @dblclick="grid.handleDoubleClick(item)"
        >
          <td
            v-for="(header, cellIndex) in grid.localHeaders"
            :key="header.key"
            class="column"
            role="gridcell"
          >
            <span v-if="cellIndex === 0 && item.type" class="visually-hidden">{{ item.type }}</span>
            <IconFolderSmall
              v-if="cellIndex === 0 && item.type === 'directory'"
              class="row-icon"
              aria-hidden="true"
            />
            <IconFileSmall
              v-else-if="cellIndex === 0 && item.type === 'file'"
              class="row-icon"
              aria-hidden="true"
            />
            <IconLinkSmall
              v-else-if="cellIndex === 0 && item.type === 'link'"
              src="Icon-Link-Small"
              class="row-icon"
              aria-hidden="true"
            />
            <DirectoryItemField
              class="field"
              :item="item"
              :field="header.key"
              :options="{ type: 'paragraph' }"
            />
          </td>
          <td class="column actions" role="gridcell">
            <template v-for="action in actions.slice(0, maxActions + 1)" :key="action.actionName">
              <ActionButton
                v-if="action.appliesOn?.includes(item.type) ?? true"
                class="row-action-button"
                :invert="grid.selectedItemIds.includes(item.id)"
                :action="action"
                :tabIndex="index === grid.focusedRowIndex && grid.gridHasFocus ? 0 : -1"
                stop-click-propagation
                @action="(action) => $emit('action', action.actionName, [item])"
              />
            </template>
            <ActionMenu
              v-if="actions.length - 1 > maxActions"
              :item="item"
              :actions="actions.slice(maxActions + 1)"
              :invert="grid.selectedItemIds.includes(item.id)"
              :tabIndex="index === grid.focusedRowIndex && grid.gridHasFocus ? 0 : -1"
              stop-click-propagation
              @close="grid.handleCloseMenu"
              @action="(act, it) => $emit('action', act.actionName, it ? [it] : [])"
            >
              <template #button>
                <ActionButton
                  v-if="actions.length"
                  :action="{
                    actionName: 'more',
                    appliesOn: [],
                    icon: 'IconMoreVertical',
                    labelKey: 'general__text__more-actions'
                  }"
                  :invert="grid.selectedItemIds.includes(item.id)"
                  :tabIndex="index === grid.focusedRowIndex && grid.gridHasFocus ? 0 : -1"
                />
              </template>
            </ActionMenu>
          </td>
        </tr>
      </TransitionGroup>
    </table>
  </div>
</template>

<script lang="ts" setup>
import { ref, toRefs, watch, onUnmounted, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDirectoryGrid, type GridOptions } from './useDirectoryGrid'
import { useMapboxFeatureState } from '@/pinia/mapboxFeatureState'
import type { Action, DirectoryItem, DirectoryItemType, Header } from '../types'
import ChevronToggle from '@/design-system/atoms/ChevronToggle.vue'
import TextBase from '@/design-system/atoms/TextBase.vue'
import ActionMenu from '../ActionMenu.vue'
import ActionButton from '../ActionButton.vue'
import IconFolderSmall from '@/design-system/assets/icons/IconFolderSmall.vue'
import IconFileSmall from '@/design-system/assets/icons/IconFileSmall.vue'
import IconLinkSmall from '@/design-system/assets/icons/IconLinkSmall.vue'
import DirectoryItemField from '../DirectoryItemField.vue'

type Props = {
  headers?: Header[]
  showHeaders?: boolean
  directoryItems: DirectoryItem[]
  actions?: Action[]
  maxActions?: number
  ariaLabel?: string
  currentItemId?: string
  dragDropEnabled?: boolean
  multiSelectEnabled?: boolean
  disabledTypes?: DirectoryItemType[]
  showFooter?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  actions: () => [],
  headers: () => [{ key: 'name', translationKey: 'name', labelKey: 'general__text__name' }],
  disabledTypes: () => [],
  showHeaders: false,
  maxActions: 3,
  ariaLabel: 'Directory content',
  currentItemId: undefined,
  showFooter: false
})
const { actions } = toRefs(props)

const emit = defineEmits<{
  (e: 'select', items: DirectoryItem[]): void
  (e: 'doubleClick', item: DirectoryItem): void
  (e: 'action', action: string, items: DirectoryItem[]): void
  (e: 'sort', headers: Header[]): void
  (e: 'itemsDroppedOnTarget', payload: { draggedItemIds: string[]; targetItemId: string }): void
  (e: 'loadMoreItems'): void
}>()

const { t } = useI18n()
const { resetRecords, setRecords } = useMapboxFeatureState()

const gridComponentRootRef = ref<HTMLElement>()
const gridBodyRef = ref<HTMLElement>()
const gridTableRef = ref<HTMLTableElement>()
const customDragImageRef = ref<HTMLElement>()

const gridOptions: GridOptions = {
  getGridBodyHeight: () => gridBodyRef.value?.clientHeight ?? 0,
  isGridFocused: () => gridComponentRootRef.value?.contains(document.activeElement) ?? false,
  scrollToRow: (id: string) => {
    document.getElementById(id)?.scrollIntoView({ block: 'nearest' })
  },
  setDragImage: (dataTransfer: DataTransfer) => {
    if (customDragImageRef.value) {
      dataTransfer.setDragImage(customDragImageRef.value, 20, 20)
    }
  },
  focusGrid: () => {
    const tableEl = gridComponentRootRef.value?.querySelector('table')
    tableEl?.focus()
  }
}

const grid = useDirectoryGrid(toRefs(props), emit, gridOptions)

function checkItemFilling() {
  if (gridComponentRootRef.value && gridTableRef.value) {
    const rootSize = gridComponentRootRef.value.getBoundingClientRect()
    const tableSize = gridTableRef.value.getBoundingClientRect()
    const tableLargerThanRoot = tableSize.height > rootSize.height
    if (!tableLargerThanRoot) {
      emit('loadMoreItems')
    }
  }
}

function checkScrollBottom() {
  if (gridComponentRootRef.value) {
    const scrollTop = gridComponentRootRef.value.scrollTop
    const scrollHeight = gridComponentRootRef.value.scrollHeight
    const clientHeight = gridComponentRootRef.value.clientHeight
    const atBottom = scrollTop + clientHeight >= scrollHeight - 1 // Allow a small buffer
    if (atBottom) {
      emit('loadMoreItems')
    }
  }
}

onMounted(() => {
  checkItemFilling()
  if (gridComponentRootRef.value) {
    new ResizeObserver(() => {
      checkItemFilling()
    }).observe(gridComponentRootRef.value)
    gridComponentRootRef.value.addEventListener('scroll', checkScrollBottom)
  }
  if (gridTableRef.value) {
    new ResizeObserver(() => {
      checkItemFilling()
    }).observe(gridTableRef.value)
  }
})

onUnmounted(() => {
  resetRecords()
})

watch(
  () => [grid.selectedItemIds, grid.hoveredItem] as const,
  ([newSelections, newHoveredItem]) => {
    const records = (newSelections ?? []).reduce((acc, id) => {
      const newItem = props.directoryItems.find((item) => item.id === id)
      return {
        ...acc,
        ...(newItem?.entityConnectionPath ?? []).reduce((innerAcc, pathId) => {
          innerAcc[pathId] = { color: '#fadb14' }
          return innerAcc
        }, {} as Record<string, { color: string }>)
      }
    }, {})
    const hoveredItemRecords = (newHoveredItem?.entityConnectionPath ?? []).reduce((acc, id) => {
      acc[id] = { color: '#fce94d' }
      return acc
    }, {} as Record<string, { color: string }>)
    setRecords({
      ...records,
      ...hoveredItemRecords
    })
  },
  { deep: true, immediate: true }
)
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/mixins.scss';

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.directory-list-view {
  width: 100%;
  background-color: #fff;
  @include mixins.scrollbar-vertical;
  overflow-x: hidden;
  overflow-y: auto;
  height: 100%;
  position: relative;

  &:focus-within {
    outline: none;
  }
}

.grid-table {
  position: relative;
  width: 100%;
  border-collapse: collapse;

  &:focus-visible {
    outline: 2px solid #3f46d6;
    outline-offset: 1px;
  }
}

.header-row,
.data-row {
  display: grid;
  align-items: center;
  min-height: 40px;
}

.header-group {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #eeeffb;
}

.header-row {
  border-bottom: 1px solid #ccc;
}

.data-row {
  color: #1a1a1a;
  transition: background-color 0.15s ease-out;

  .row-action-button {
    display: none;
  }

  &.row--focused .row-action-button,
  &:hover .row-action-button,
  &:focus-within .row-action-button {
    display: inline-flex;
  }

  &.row--selected {
    background-color: #3f46d6;
    color: #fff;
  }

  &:not(.row--selected):hover {
    background-color: #f5f8ff;
  }

  &.row--focused:not(.row--selected) {
    background-color: #e6e8ff;
    outline: 1px solid #3f46d6;
    outline-offset: -1px;
  }

  &.row--focused.row--selected {
    outline: 1px solid #d5d7f6;
    outline-offset: -1px;
  }

  &.row--disabled {
    color: #767676;
    cursor: not-allowed;
    pointer-events: none;

    .row-action-button {
      display: none;
    }
  }

  &.row--archived {
    .row-icon {
      color: #767676;
    }

    .field {
      color: #767676;
    }
  }

  &.row--archived.row--selected {
    color: #fff;

    .row-icon {
      color: #fff;
    }

    .field {
      color: #fff;
    }
  }

  &.row--dragging-source {
    opacity: 0.4;
  }

  &.row--drop-target {
    background-color: #dde0f0;
    outline: 2px dashed #3f46d6;
    outline-offset: -2px;

    &.row--selected {
      background-color: #5c62c7;
    }
  }
}

.column {
  height: 40px;
  padding: 4px 8px;
  text-decoration: inherit;
  color: inherit;
  background-color: inherit;
  display: flex;
  align-items: center;
  user-select: none;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-align: left;

  &:first-child {
    padding-left: 16px;
  }

  &:last-child {
    padding-right: 16px;
    justify-content: flex-end;
  }
}

.header {
  font-weight: normal;
  background-clip: padding-box;

  &--sortable {
    cursor: pointer;
  }

  &:focus-visible {
    outline: 2px solid #3f46d6;
    outline-offset: -2px;
    background-color: #dde0f0;
  }
}

.row-icon {
  margin-right: 8px;
  flex-shrink: 0;
}

.actions {
  color: #3f46d6;
  justify-content: flex-end;
  overflow: visible;
}

.header-chevron {
  margin-left: 8px;
  flex-shrink: 0;
}

.custom-drag-preview {
  position: absolute;
  left: -9999px;
  top: -9999px;
  z-index: 1000;
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  pointer-events: none;
  font-size: 14px;
  color: #1f1f1f;

  .drag-preview-icon {
    margin-right: 8px;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  .drag-preview-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }

  .drag-preview-badge {
    position: absolute;
    top: -8px;
    right: -8px;
    background-color: #1a73e8;
    color: white;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    font-size: 12px;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }
}

.list-move,
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.list-leave-active {
  position: absolute;
  z-index: -1;
}
</style>
